import { normalizeId } from '../form-generator/helpers/utility';
import { contractMilestoneDates, isMicasa, commissionImportantFieldHTML, salesImportantFieldHTML } from './constants';
import { addChoicesToCRMForm } from '../components/crm/form';
import {
  addChoicesToContractForm,
  additionalFieldsRow as contractAdditionalFields,
  mainFieldsRows as contractMainFieldRows,
  typeOfTransactionFields,
  validateAndCleanupForm
} from '../components/main/form';
import { getAdditionalFieldsColumns, getCustomEntityFieldsColumns } from './formHelpers';
import { getProfileFromLocalStorage } from './session';
import { sumCommissionOptionValues } from './global';

export const camelCaseToTitle = (camelCaseText = '') => {
  if (!camelCaseText) return '';

  const result = camelCaseText.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const INVENTORY_FIELD_NAMES = ['inventoryUnit', 'inventoryUnits'];
const INVENTORY_COMMISSION_PATH_KEYS = ['commissionSheetOptions', 'commissionSheetLessOptions'];

export const documentFieldsHasInventoryCommissionPaths = documentFields => {
  if (!documentFields) return false;

  const inventoryParams = [...(documentFields.withData || []), ...(documentFields.withoutData || [])].filter(param =>
    INVENTORY_FIELD_NAMES.includes(param.name)
  );

  return inventoryParams.some(({ pathsInDoc = [] }) =>
    pathsInDoc.some(path => {
      if (!Array.isArray(path) || path.length < 2) return false;
      if (!INVENTORY_FIELD_NAMES.includes(path[0])) return false;
      return INVENTORY_COMMISSION_PATH_KEYS.includes(path[path.length - 1]);
    })
  );
};

const DOCHUB_OBJECT_FIELD_NAMES = ['_id', 'additionalFields', 'signature', 'custom', 'customEntities'];

const createTextField = ({ key }) => {
  return {
    default: 6,
    xs: 12,
    field: {
      id: key,
      key,
      title: camelCaseToTitle(key),
      type: 'text',
      required: false
    }
  };
};

const titleRow = (title, className = '', withHr = true) => ({
  columns: [
    {
      default: 12,
      field: {
        type: 'custom-html',
        html: `<h6 class="mt-2 mb-0 text-muted ${className}"><b>${title}</b></h6>${
          withHr ? "<hr class='mt-1 mb-2'/>" : ''
        }`
      }
    }
  ]
});

const fieldJsonToColumns = (field, summaryFieldType, isMockPrint) => {
  if (field.key === 'typeOfTransaction') {
    return typeOfTransactionFields(true);
  }
  if (field.key === 'signature') {
    const user = getProfileFromLocalStorage();
    return {
      default: field.col || 6,
      field: {
        id: field.key,
        required: false,
        title: 'Use signature of',
        type: 'dropdown',
        options: [],
        value: user?._id
      }
    };
  }
  if (field.key === 'mailingZip' || field.key === 'zipCode') {
    return {
      default: field.col || 6,
      field: {
        id: field.key,
        onTextChange: field.key === 'mailingZip' ? 'autoFillMailingAddress' : 'autoFillpropertyAddress',
        required: false,
        title: field.key === 'mailingZip' ? 'Mailing Zip' : 'Property Zip',
        type: 'text'
      }
    };
  }
  const searchFields = [...contractMainFieldRows().flatMap(r => r.columns), ...contractAdditionalFields.columns];
  //if is date field, return all related fields
  const contractMilestoneDate = contractMilestoneDates.find(d => d.key === field.key);
  if (contractMilestoneDate) {
    const relatedFieldKeys = ['start', 'end', 'emailField', 'options'];
    return relatedFieldKeys
      .map(k =>
        searchFields.find(
          searchField =>
            searchField.field.key === contractMilestoneDate[k] || searchField.field.id === contractMilestoneDate[k]
        )
      )
      .filter(field => Boolean(field));
  }

  const filteredColumn =
    searchFields.find(searchField => searchField.field.key === field.key || searchField.field.id === field.key) ||
    createTextField(field);

  const filteredField = filteredColumn.field;

  const returnColumn = {
    default: field.col || (filteredField.type === 'key-value' ? 12 : 6),
    xs: 12,
    field: filteredField
  };

  if (summaryFieldType) {
    let onChange;

    if (summaryFieldType === 'Sales Agreement' && calculateSalesAgreementFields.includes(field.key)) {
      onChange = 'calculateSalesSummary';
    } else if (calculateCommissionSheetFields.includes(field.key)) {
      onChange = 'calculateCommissionsSummary';
    }
    returnColumn['field'][['text', 'number'].includes(filteredField.type) ? 'onTextChange' : 'onChange'] = onChange;
  }

  returnColumn.field.required = false;
  returnColumn.field.defaultHideOptions = false;

  return [returnColumn];
};

const salesAgreementFields = [
  {
    width: '50%',
    columns: [
      {
        key: 'salesLocation',
        col: 12
      }
    ]
  },
  {
    width: '50%',
    columns: [
      {
        key: 'lender',
        col: 12
      },
      {
        key: 'applicationNumber',
        col: 12
      }
    ]
  },
  {
    width: '50%',
    columns: [
      {
        key: 'buyer',
        col: 12
      },
      {
        key: 'coBuyer',
        col: 6
      },
      {
        key: 'buyer3',
        col: 6
      },
      {
        key: 'mailingAddress',
        col: 12
      },
      {
        key: 'mailingCity',
        col: 4
      },
      {
        key: 'mailingState',
        col: 4
      },
      {
        key: 'mailingZip',
        col: 4
      }
    ]
  },
  {
    width: '50%',
    columns: [
      {
        key: 'salesperson',
        col: 12
      },
      {
        key: 'consultant',
        col: 12
      },
      {
        key: 'dateSigned',
        col: 12
      },

      {
        key: 'cellPhone',
        col: 12
      },

      {
        key: 'cellPhone2',
        col: 12
      }
    ]
  },
  {
    columns: [
      {
        key: 'newOrUsed',
        col: 6
      },
      {
        key: 'tradeSqFt',
        col: 6
      }
    ]
  },
  {
    columns: [
      //{{propertyAddress}}, {{propertyCity}}, {{propertyState}}, {{zipCode}} {{propertyCounty}} {{buyerEmail}}			{{coBuyerEmail}}				{{stockOrRso}}
      {
        key: 'propertyAddress',
        col: 2
      },
      {
        key: 'propertyCity',
        col: 1
      },
      {
        key: 'propertyState',
        col: 1
      },
      {
        key: 'zipCode',
        col: 1
      },
      {
        key: 'propertyCounty',
        col: 2
      },
      {
        key: 'buyerEmail',
        col: 2
      },
      {
        key: 'coBuyerEmail',
        col: 2
      },
      {
        key: 'stockOrRso',
        col: 1
      },
      //{{tradeSerial}}	{{tradeHud}} {{tradeYear}}	{{tradeSize}}	{{tradeBrBa}}
      {
        key: 'tradeSerial',
        col: 3
      },
      {
        key: 'tradeHud',
        col: 3
      },
      {
        key: 'tradeYear',
        col: 2
      },
      {
        key: 'tradeSize',
        col: 2
      },
      {
        key: 'tradeBrBa',
        col: 2
      }
    ]
  },
  {
    width: '50%',
    columns: [
      {
        key: 'optionalEquipmentAndAccessories',
        col: 12
      },
      {
        key: 'retailerOptions',
        col: 12
      },
      {
        key: 'sellerPaidOptions',
        col: 12
      },
      {
        key: 'promiseListItems',
        col: 12
      }
    ]
  },
  {
    width: '50%',
    columns: [
      {
        key: 'salesPrice',
        col: 12
      },
      {
        key: 'tradeInGrossAllowance',
        col: 12
      },
      {
        key: 'lessBalanceDue',
        col: 12
      },
      {
        key: 'cashAsAgreed',
        col: 12
      },
      {
        key: 'downPayment',
        col: 12
      },
      {
        key: 'floodCertificationCosts',
        col: 12
      },
      {
        key: 'appraisalCosts',
        col: 12
      },
      {
        key: 'improvementCosts',
        col: 12
      },
      {
        key: 'insuranceCosts',
        col: 12
      },
      {
        key: 'formTCosts',
        col: 12
      },
      {
        key: 'titleFeesCosts',
        col: 12
      },
      {
        key: 'taxFactorAdjustedSalesPrice',
        col: 12
      },
      {
        key: 'closingDocPrepFedexNotary',
        col: 12
      },
      {
        key: 'otherCosts',
        col: 12
      },
      {
        key: 'originationPoints',
        col: 12
      }
    ]
  }
];

const isMobileDevice = () => {
  return window.innerWidth < 992; // Bootstrap's md breakpoint is 992px
};

export const getDocHubFormJSON = (
  { withData = [], withoutData = [], new: newFields = [] },
  summaryFieldType,
  appChoices,
  docSource,
  isMockPrint,
  initialFormValues,
  documentName
) => {
  const isMobile = isMobileDevice();
  const isSalesAgreement =
    documentName?.toLowerCase().includes('sales agreement') ||
    summaryFieldType?.toLowerCase().includes('sales agreement');

  const allFieldNames = [...withData, ...withoutData, ...newFields].map(f => f.name);

  const salesAgreementFieldsToUse = isSalesAgreement ? salesAgreementFields : [];
  const salesAgreementColumnNames = salesAgreementFieldsToUse.flatMap(f => f.columns.map(c => c.key));

  const salesAgreementRows = !isSalesAgreement
    ? []
    : salesAgreementFieldsToUse
        .map(f => ({
          width: isSalesAgreement && f.width && !isMobile ? f.width : '100%',
          className: 'py-3',
          columns: f.columns
            .filter(c => !DOCHUB_OBJECT_FIELD_NAMES.includes(c.key) && allFieldNames.includes(c.key))
            .flatMap(c =>
              fieldJsonToColumns(
                {
                  key: c.key,
                  value: withData.find(d => d.name === c.key)?.value,
                  col: isSalesAgreement && !isMobile ? c.col : undefined
                },
                summaryFieldType,
                isMockPrint
              )
            )
        }))
        .filter(f => f.columns.length > 0);

  const withDataColumns = withData
    .filter(
      f => !DOCHUB_OBJECT_FIELD_NAMES.includes(f.name) && !salesAgreementColumnNames.includes(f.name)
    )
    .flatMap(f => fieldJsonToColumns({ key: f.name, value: f.value }, summaryFieldType, isMockPrint));

  const withoutDataColumns = withoutData
    .filter(
      f => !DOCHUB_OBJECT_FIELD_NAMES.includes(f.name) && !salesAgreementColumnNames.includes(f.name)
    )
    .flatMap(f => fieldJsonToColumns({ key: f.name }, summaryFieldType, isMockPrint));

  const newFieldsColumns = newFields
    .filter(f => !DOCHUB_OBJECT_FIELD_NAMES.includes(f) && !salesAgreementColumnNames.includes(f))
    .flatMap(f => fieldJsonToColumns({ key: f }, summaryFieldType, isMockPrint));

  const rows = [];

  if (salesAgreementRows.length > 0) {
    rows.push(...salesAgreementRows);
  }

  if (withDataColumns.length > 0) {
    rows.push(titleRow(`Existing Fields from ${docSource}`), { columns: withDataColumns });
  }
  if (withoutDataColumns.length > 0) {
    rows.push(titleRow(`Empty Fields from ${docSource}`), { columns: withoutDataColumns });
  }
  if (newFieldsColumns.length > 0) {
    rows.push(titleRow('New Fields'), { columns: newFieldsColumns });
  }

  const additionalFieldsInWithData = withData.find(c => c.name === 'additionalFields');
  const additionalFieldsInWithOutData = withoutData.find(c => c.name === 'additionalFields');
  const additionalFieldsToUse = additionalFieldsInWithData || additionalFieldsInWithOutData;

  if (additionalFieldsToUse) {
    const additionalFieldKeysInDoc = additionalFieldsToUse.pathsInDoc.map(p => p[p.length - 1]);

    const additionalFieldColumns = getAdditionalFieldsColumns({
      appChoices,
      model: docSource,
      keysToUse: additionalFieldKeysInDoc
    });

    if (additionalFieldColumns.length > 0) {
      rows.push(titleRow('Additional DocHub Fields'), {
        columns: additionalFieldColumns.map(c => {
          let onChange;
          if (summaryFieldType) {
            if (summaryFieldType === 'Sales Agreement' && calculateSalesAgreementFields.includes(c.field.key)) {
              onChange = 'calculateSalesSummary';
            } else if (calculateCommissionSheetFields.includes(c.field.key)) {
              onChange = 'calculateCommissionsSummary';
            }
          }

          return {
            ...c,
            field: {
              ...c.field,
              [['text', 'number'].includes(c.field.type) ? 'onTextChange' : 'onChange']: onChange
            }
          };
        })
      });
    }
  }

  const customFieldsToUse =
    withData.find(c => c.name === 'customEntities') ||
    withoutData.find(c => c.name === 'customEntities') ||
    withData.find(c => c.name === 'custom') ||
    withoutData.find(c => c.name === 'custom');

  if (customFieldsToUse) {
    const namesToUse = [
      ...new Set(
        (customFieldsToUse.pathsInDoc || [])
          .map(path => {
            if (path?.[0] === 'inventoryUnit' && (path[1] === 'custom' || path[1] === 'customEntities')) {
              return path[2];
            }
            return path?.[1];
          })
          .filter(Boolean)
      )
    ];

    const customEntityColumns = getCustomEntityFieldsColumns({
      appChoices,
      model: docSource,
      namesToUse: namesToUse.length ? namesToUse : undefined
    });

    if (customEntityColumns.length > 0) {
      rows.push(titleRow('Custom Fields'), { columns: customEntityColumns });
    }
  }

  const signatureField = withoutData.find(c => c.name === 'signature');
  if (signatureField) {
    rows.push(
      titleRow('Signature (Create a signature from your profile <a href="/profile" target="_blank">here</a>)'),
      {
        columns: [fieldJsonToColumns({ key: 'signature' })]
      }
    );
  }

  if (rows.length === 0) {
    rows.push(titleRow('No parameters to fill!', 'text-muted text-center p-4', false));
  }

  const documentFormJSON = {
    forms: [
      {
        name: 'document',
        hideFormName: true,
        markCompulsoryFields: true,
        compact: true,
        submit: {
          name: 'Print',
          show: false,
          onSubmit: 'onPrintSubmit'
        },
        rows: [
          ...rows,
          ...(summaryFieldType
            ? [
                {
                  columns: [
                    {
                      default: 12,
                      field: {
                        type: 'custom-html',
                        html:
                          summaryFieldType === 'Commission Sheet'
                            ? commissionImportantFieldHTML
                            : salesImportantFieldHTML
                      }
                    }
                  ]
                }
              ]
            : [])
        ]
      }
    ]
  };

  addChoicesToDochubForm(documentFormJSON, appChoices, initialFormValues);

  return documentFormJSON;
};

export const calculateCommissionsSummary = (inventoryUnit = null) => {
  const salesPrice = Number(document.getElementById('salesPrice')?.value || 0);
  const inventoryInvoice = Number(inventoryUnit?.invoice || 0);
  const commissionLessValue = sumCommissionOptionValues(inventoryUnit?.commissionSheetLessOptions);

  const netCommissionsTotal = salesPrice - commissionLessValue;

  const optionsTotal =
    inventoryInvoice + sumCommissionOptionValues(inventoryUnit?.commissionSheetOptions);

  const grossProfit = netCommissionsTotal - optionsTotal;
  let commissionDue;

  let bonus = 0;
  const bonusType = document.getElementById('bonusType-additionalDochubField')?.value || 'Bonus';

  if (isMicasa) {
    bonus = Number(document.getElementById('bonus-additionalDochubField')?.value || 0);
    commissionDue = grossProfit >= 20000 ? 0.25 * grossProfit : 0.2 * grossProfit;
  } else {
    commissionDue = grossProfit / 2;
  }

  const curtailmentBonus = Number(document.getElementById('curtailmentBonus-additionalDochubField')?.value || 0);
  const teamLeaderBonus = Number(document.getElementById('teamLeaderBonus-additionalDochubField')?.value || 0);

  const commissionDueWithBonus = commissionDue + curtailmentBonus + teamLeaderBonus;

  const totalWithBonus = commissionDue + bonus;
  const frontEnd = commissionDueWithBonus / 2;

  const firstHalfPaid = Number(document.getElementById('firstHalfPaid-additionalDochubField')?.value || 0);
  const backendAdvancePaid = Number(document.getElementById('backendAdvancePaid-additionalDochubField')?.value || 0);

  const balanceToBePaid = commissionDueWithBonus - firstHalfPaid;
  const balanceAfterAdvance = balanceToBePaid - backendAdvancePaid;

  document.getElementById('netCommission').innerHTML = `$${netCommissionsTotal.toFixed(2)}`;
  document.getElementById('totalOfOptions').innerHTML = `$${optionsTotal.toFixed(2)}`;
  document.getElementById('grossProfit').innerHTML = `$${grossProfit.toFixed(2)}`;
  document.getElementById('commissionDue').innerHTML = `$${commissionDue.toFixed(2)}`;
  document.getElementById('commissionDueWithBonus').innerHTML = `$${commissionDueWithBonus.toFixed(2)}`;
  document.getElementById('frontEnd').innerHTML = `$${frontEnd.toFixed(2)}`;

  document.getElementById('balanceToBePaid').innerHTML = `$${balanceToBePaid.toFixed(2)}`;
  document.getElementById('balanceAfterAdvance').innerHTML = `$${balanceAfterAdvance.toFixed(2)}`;

  document.getElementById('backEnd').innerHTML = `$${frontEnd.toFixed(2)}`;

  if (document.getElementById('bonusTypeName')) {
    document.getElementById('bonusTypeName').innerHTML = bonusType;
    document.getElementById('bonusValue').innerHTML = `$${bonus.toFixed(2)}`;
    document.getElementById('totalWithBonusValue').innerHTML = `$${totalWithBonus.toFixed(2)}`;
  }
};

export const calculateSalesSummary = () => {
  const optionalValues = getTabularOptionsFieldValue('optionalEquipmentAndAccessories');

  const salesPrice = Number(document.getElementById('salesPrice').value);
  //sales price + options
  const subTotal = optionalValues + salesPrice;

  //Trade-In Gross Allowance - Less Balance Due
  const tradeInGrossAllowance = Number(document.getElementById('tradeInGrossAllowance').value);
  const lessBalanceDue = Number(document.getElementById('lessBalanceDue').value);
  const tradeNetAllowance = tradeInGrossAllowance - lessBalanceDue;

  //Trade-In Net Allowance + Cash As Agreed	+ Down Payment
  const cashAsAgreed = Number(document.getElementById('cashAsAgreed').value);
  const downPayment = Number(document.getElementById('downPayment').value);
  const totalDownPayment = tradeNetAllowance + cashAsAgreed + downPayment;

  // subtotal - total downpayment
  const unpaidCashPrice = subTotal - totalDownPayment;

  //total unpaid balance = unpaid cash + unpaid fields
  const totalUnpaidBalance =
    unpaidCashPrice +
    unpaidFields
      .map(f => {
        try {
          return Number(document.getElementById(f).value);
        } catch (error) {
          return 0;
        }
      })
      .reduce((prevValue, newValue) => {
        return prevValue + newValue;
      });

  //totalunpaid - Origination Points
  const originationPoints = Number(document.getElementById('originationPoints').value);
  const totalBalanceDue = totalUnpaidBalance - originationPoints;

  document.getElementById('options').innerHTML = `$${optionalValues.toFixed(2)}`;
  document.getElementById('subTotal').innerHTML = `$${subTotal.toFixed(2)}`;
  document.getElementById('tradeInNetAllowance').innerHTML = `$${tradeNetAllowance.toFixed(2)}`;
  document.getElementById('totalDownPayment').innerHTML = `$${totalDownPayment.toFixed(2)}`;
  document.getElementById('unpaidCashPrice').innerHTML = `$${unpaidCashPrice.toFixed(2)}`;
  document.getElementById('totalUnpaidBalance').innerHTML = `$${totalUnpaidBalance.toFixed(2)}`;
  document.getElementById('totalBalanceDue').innerHTML = `$${totalBalanceDue.toFixed(2)}`;
};

export const getKeyValueFieldsValue = inputId => {
  try {
    const keyValueContainer = document.getElementById(inputId);

    const checkedKeys = [...keyValueContainer.getElementsByTagName('input')].filter(
      input => input.type === 'checkbox' && input.checked
    );

    const values = checkedKeys
      .map(input => ({ checked: input.checked, value: input.value }))
      .map(key => {
        try {
          return Number(document.getElementById(`${inputId}-${normalizeId(key.value)}-key-value`).value);
        } catch (error) {
          return 0;
        }
      });

    return values.length > 0 ? values.reduce((prevValue, newValue) => prevValue + newValue) : 0;
  } catch (e) {
    return 0;
  }
};

export const getTabularOptionsFieldValue = inputId => {
  try {
    const table = document.getElementById(inputId);
    var tbodyRowCount = table.tBodies[0]?.rows.length || 0;

    let value = 0;
    const normalizedId = normalizeId(inputId);
    for (let index = 0; index < tbodyRowCount; index++) {
      const valueInput = Number(document.getElementById(`${normalizedId}-value-${index}`).value);
      value = value + (isNaN(valueInput) ? 0 : valueInput);
    }

    return value;
  } catch (e) {
    return 0;
  }
};

const addChoicesToDochubForm = (form, appChoices, initialFormValues) => {
  addChoicesToContractForm({ form, appChoices, initialFormValues });
  addChoicesToCRMForm({ form, appChoices, initialFormValues });
};

export const netCommissionsFieldKeys = ['adminCosts', 'formTCosts', 'mhitCosts', 'titleFeesCosts'];
export const optionsFieldKeys = [
  'acquisitionCosts',
  'adminCosts',
  'stepsCosts',
  'deliveryCosts',
  'newAcCosts',
  'skirtingCosts',
  'floorplanCosts',
  'mileageCosts',
  'breakdownCosts',
  'improvementCosts',
  'calicheBasePadCosts',
  'houseCatCosts',
  'hookupCosts',
  'tntCosts',
  'otherCosts'
];

export const commissionSheetFieldKeys = [
  'buyer',
  'lender',
  'consultant',
  'typeOfUnit',
  'address',
  'city',
  'state',
  'zip',
  'cell#',
  'salesCenter',
  'deliveryDate',
  'fundedDate',
  ...netCommissionsFieldKeys,
  ...optionsFieldKeys
];

export const unpaidFields = [
  'floodCertificationCosts',
  'appraisalCosts',
  'improvementCosts',
  'insuranceCosts',
  'formTCosts',
  'titleFeesCosts',
  'taxFactorAdjustedSalesPrice',
  'otherCosts',
  'originationPoints',
  'closingDocPrepFedexNotary'
];

const unitInsulationFields = [
  'ceilingRValues',
  'ceilingThickness',
  'wallsRValues',
  'wallsThickness',
  'floorRValues',
  'floorThickness'
];

const tradeInFields = ['tradeInGrossAllowance', 'lessBalanceDue', 'cashAsAgreed', 'downPayment'];

export const calculateSalesAgreementFields = [
  'salesPrice',
  ...unitInsulationFields,
  'optionalEquipmentAndAccessories',
  ...tradeInFields,
  ...unpaidFields
];

export const calculateCommissionSheetFields = [
  'salesPrice',
  'bonusType-additionalDochubField',
  'bonus-additionalDochubField',
  'curtailmentBonus-additionalDochubField',
  'teamLeaderBonus-additionalDochubField',
  'firstHalfPaid-additionalDochubField',
  'backendAdvancePaid-additionalDochubField'
];

export const salesAgreementSheetFields = [
  'salesLocation',
  'lender',
  'applicationNumber',
  'buyer',
  'buyerEmail',
  'consultant',
  'coBuyer',
  'coBuyerName',
  'coBuyerEmail',
  'dateSigned',
  'address',
  'cellPhone',
  'cellPhone2',
  'city',
  'state',
  'zip',
  'size',
  'county',
  'propertyAddress',
  'propertyCity',
  'propertyState',
  'zipCode',
  'newOrUsed',
  'areaSqft)',
  'typeOfUnit',
  'stockOrRSO',
  ...calculateSalesAgreementFields
];

export const validateAndCleanupDocHubForm = form => {
  const contractValid = validateAndCleanupForm(form);

  return contractValid;
};
